import { z, ZodError } from 'zod';
import packageJson from '../../../package.json';
import {
  db,
  AppDB,
  type ReportRow,
  type CompanyRow,
  type SiteRow,
  type ContactRow,
  type UnitRow,
  type ActionRow,
  type SchemaRow,
  type TemplateRow,
  type KvRow,
  type MetaRow,
  type MediaRow,
  type ComponentCategoryRow,
} from '../db';

const DATA_BUNDLE_SIGNATURE = 'industrial-audit-system:data-export';
const DATA_BUNDLE_VERSION = 3;

const APP_VERSION = typeof packageJson?.version === 'string' ? packageJson.version : undefined;

const FEATURE_FLAGS = {
  offlineDataBundles: true,
  devMode: typeof import.meta !== 'undefined' ? (import.meta.env.DEV ?? false) : false,
} as const;

export class DataBundleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DataBundleError';
  }
}

type SerializedReportRow = Omit<ReportRow, 'payload'> & {
  payload: string;
};

type SerializedMediaRow = Omit<MediaRow, 'blob'> & {
  dataUrl: string | null;
};

interface DataBundleTables {
  reports: SerializedReportRow[];
  companies: CompanyRow[];
  sites: SiteRow[];
  contacts: ContactRow[];
  units: UnitRow[];
  actions: ActionRow[];
  schemas: SchemaRow[];
  templates: TemplateRow[];
  componentCategories: ComponentCategoryRow[];
  kv: KvRow[];
  meta: MetaRow[];
  media: SerializedMediaRow[];
}

export type DataBundleCounts = {
  [K in keyof DataBundleTables]: number;
};

export interface DataBundleMeta {
  signature: typeof DATA_BUNDLE_SIGNATURE;
  version: number;
  createdAt: string;
  schemaVersion: number;
  appVersion?: string;
  featureFlags: Record<string, boolean>;
  counts: DataBundleCounts;
}

export interface DataBundle {
  meta: DataBundleMeta;
  tables: DataBundleTables;
}

const isoDateSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), {
  message: 'meta.createdAt deve essere una data ISO valida',
});

const syncMetadataSchema = z.object({
  syncUpdatedAt: isoDateSchema,
  syncDeletedAt: isoDateSchema.optional(),
  syncDeviceId: z.string().optional(),
  syncVersion: z.number().int(),
});

const serializedReportRowSchema = z
  .object({
    id: z.string(),
    version: z.number().int(),
    companyId: z.string(),
    siteId: z.string(),
    payload: z.string(),
    lockedBy: z.string().optional(),
    lockedAt: isoDateSchema.optional(),
    lockedDevice: z.string().optional(),
  })
  .merge(syncMetadataSchema)
  .passthrough();

const companyRowSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
  })
  .merge(syncMetadataSchema)
  .passthrough();

const siteRowSchema = z
  .object({
    id: z.string(),
    companyId: z.string(),
    name: z.string(),
    sequenceNumber: z.number().int().min(1),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
  })
  .merge(syncMetadataSchema)
  .passthrough();

const contactRowSchema = z
  .object({
    id: z.string(),
    companyId: z.string(),
    siteId: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    role: z.string().optional(),
    scope: z.enum(['company', 'site']).optional(),
    reportRecipient: z.boolean().optional(),
    alarmContact: z.boolean().optional(),
    fireTraining: z.boolean().optional(),
    firstAidTraining: z.boolean().optional(),
    licensed: z.boolean().optional(),
    licenseExpiry: z.string().optional(),
    licenseLastUpdate: z.string().optional(),
    reperibile: z.boolean().optional(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
    deletedAt: isoDateSchema.optional(),
  })
  .merge(syncMetadataSchema)
  .passthrough();

const unitRowSchema = z
  .object({
    id: z.string(),
    companyId: z.string(),
    siteId: z.string(),
    refrigerationUnitId: z.string(),
    name: z.string(),
    location: z.unknown().optional(),
    reperibilePhone: z.string().optional(),
    ammoniaKg: z.number().optional(),
    toxicGasType: z.unknown().optional(),
    impiantoType: z.unknown().optional(),
    plannedPeriod: z.string().optional(),
    plannedDate: z.string().optional(),
    plannedSource: z.string().optional(),
    plannedUpdatedAt: z.string().optional(),
    technical: z.custom<UnitRow['technical']>(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
    deletedAt: isoDateSchema.optional(),
  })
  .merge(syncMetadataSchema)
  .passthrough();

const actionRowSchema = z
  .object({
    id: z.string(),
    companyId: z.string(),
    siteId: z.string().optional(),
    reportId: z.string().optional(),
    reportTitle: z.string().optional(),
    reportVisitDate: z.string().optional(),
    reportSiteName: z.string().optional(),
    title: z.string(),
    type: z.enum(['internal', 'external', 'system']),
    priority: z.enum(['low', 'medium', 'high']).optional(),
    status: z.enum(['pending', 'in_progress', 'completed', 'open']).transform((val) =>
      // Map legacy "open" to "pending" during import
      val === 'open' ? 'pending' : val
    ),
    dueDate: z.string().optional(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
  })
  .merge(syncMetadataSchema)
  .passthrough();

const schemaRowSchema = z
  .object({
    id: z.string(),
    version: z.string(),
    definition: z.unknown(),
    isDefault: z.boolean(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
  })
  .merge(syncMetadataSchema)
  .passthrough();

const templateRowSchema = z
  .object({
    _key: z.string(),
    id: z.string(),
    version: z.string(),
    name: z.string(),
    description: z.string().optional(),
    sections: z.array(z.unknown()),
    metadata: z
      .object({
        targetType: z.literal('ammonia-audit'),
        createdAt: isoDateSchema,
        updatedAt: isoDateSchema,
      })
      .passthrough(),
  })
  .merge(syncMetadataSchema)
  .passthrough();

const componentCategoryRowSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    description: z.string().optional(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
  })
  .merge(syncMetadataSchema)
  .passthrough();

const kvRowSchema = z
  .object({
    key: z.string(),
    value: z.unknown(),
  })
  .passthrough();

const metaRowSchema = z
  .object({
    key: z.string(),
    value: z.unknown(),
  })
  .passthrough();

const mediaRowSchema = z
  .object({
    id: z.string(),
    reportId: z.string(),
    storagePath: z.string().optional(),
    dataUrl: z.string().nullable(),
    mimeType: z.string().optional(),
    byteSize: z.number().optional(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
  })
  .passthrough();

const bundleSchema = z.object({
  meta: z.object({
    signature: z.literal(DATA_BUNDLE_SIGNATURE),
    version: z.literal(DATA_BUNDLE_VERSION),
    createdAt: isoDateSchema,
    schemaVersion: z.number().int(),
    appVersion: z.string().optional(),
    featureFlags: z.record(z.string(), z.boolean()),
    counts: z.object({
      reports: z.number().int(),
      companies: z.number().int(),
      sites: z.number().int(),
      contacts: z.number().int(),
      units: z.number().int(),
      actions: z.number().int(),
      schemas: z.number().int(),
      templates: z.number().int(),
      componentCategories: z.number().int().default(0),
      kv: z.number().int(),
      meta: z.number().int(),
      media: z.number().int(),
    }),
  }),
  tables: z.object({
    reports: z.array(serializedReportRowSchema),
    companies: z.array(companyRowSchema),
    sites: z.array(siteRowSchema),
    contacts: z.array(contactRowSchema),
    units: z.array(unitRowSchema),
    actions: z.array(actionRowSchema),
    schemas: z.array(schemaRowSchema),
    templates: z.array(templateRowSchema),
    componentCategories: z.array(componentCategoryRowSchema).default([]),
    kv: z.array(kvRowSchema),
    meta: z.array(metaRowSchema).default([]),
    media: z.array(mediaRowSchema).default([]),
  }),
});

type IdleCallback = (callback: () => void) => number;

function currentSchemaVersion(database: AppDB): number {
  return Number.isFinite(database.verno) ? Math.trunc(database.verno) : 0;
}

async function waitForIdle(): Promise<void> {
  const idleCallback = (globalThis as { requestIdleCallback?: IdleCallback }).requestIdleCallback;

  if (typeof idleCallback === 'function') {
    await new Promise<void>((resolve) => {
      idleCallback(() => resolve());
    });
    return;
  }

  await new Promise<void>((resolve) => setTimeout(resolve, 16));
}

async function resolveReportPayload(payload: ReportRow['payload']): Promise<string> {
  const candidate = payload as unknown;

  if (candidate instanceof Blob) {
    if (typeof candidate.text === 'function') {
      return await candidate.text();
    }
    if (typeof candidate.arrayBuffer === 'function') {
      const buffer = await candidate.arrayBuffer();
      return new TextDecoder().decode(new Uint8Array(buffer));
    }
    if (typeof Response === 'function') {
      const response = new Response(candidate);
      return await response.text();
    }
  }

  if (candidate && typeof (candidate as { text?: unknown }).text === 'function') {
    return await (candidate as { text: () => Promise<string> }).text();
  }

  if (candidate instanceof ArrayBuffer) {
    return new TextDecoder().decode(new Uint8Array(candidate));
  }

  if (candidate instanceof Uint8Array) {
    return new TextDecoder().decode(candidate);
  }

  if (typeof candidate === 'string') {
    return candidate;
  }

  throw new DataBundleError("Impossibile serializzare il payload del report per l'export.");
}

function dataUrlToBlob(dataUrl: string | null): Blob | null {
  if (!dataUrl) return null;
  const [header, base64] = dataUrl.split(',', 2);
  if (!header || !base64) {
    throw new DataBundleError('Media non valido nel file di export.');
  }
  const match = header.match(/data:(.*?);base64/);
  const mimeType = match?.[1] ?? 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function serializeReports(database: AppDB): Promise<SerializedReportRow[]> {
  const rows = await database.reports.toArray();
  const serialized: SerializedReportRow[] = [];
  for (const row of rows) {
    serialized.push({
      id: row.id,
      version: row.version,
      companyId: row.companyId,
      siteId: row.siteId,
      payload: await resolveReportPayload(row.payload),
      lockedBy: row.lockedBy,
      lockedAt: row.lockedAt,
      lockedDevice: row.lockedDevice,
      syncUpdatedAt: row.syncUpdatedAt,
      syncDeletedAt: row.syncDeletedAt,
      syncDeviceId: row.syncDeviceId,
      syncVersion: row.syncVersion,
    });
  }
  return serialized;
}

async function serializeMedia(database: AppDB): Promise<SerializedMediaRow[]> {
  const rows = (await database.media?.toArray()) ?? [];
  const serialized: SerializedMediaRow[] = [];

  for (const row of rows) {
    const dataUrl = row.blob ? await blobToDataUrl(row.blob) : null;
    serialized.push({
      id: row.id,
      reportId: row.reportId,
      storagePath: row.storagePath,
      dataUrl,
      mimeType: row.mimeType,
      byteSize: row.byteSize,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    });
  }

  return serialized;
}

function buildCounts(tables: DataBundleTables): DataBundleCounts {
  return {
    reports: tables.reports.length,
    companies: tables.companies.length,
    sites: tables.sites.length,
    contacts: tables.contacts.length,
    units: tables.units.length,
    actions: tables.actions.length,
    schemas: tables.schemas.length,
    templates: tables.templates.length,
    componentCategories: tables.componentCategories.length,
    kv: tables.kv.length,
    meta: tables.meta.length,
    media: tables.media.length,
  } satisfies DataBundleCounts;
}

export async function captureDatabaseBundle(database: AppDB = db): Promise<DataBundle> {
  const schemaVersion = currentSchemaVersion(database);

  const reports = await serializeReports(database);
  await waitForIdle();
  const companies = await database.companies.toArray();
  await waitForIdle();
  const sites = await database.sites.toArray();
  await waitForIdle();
  const contacts = await database.contacts.toArray();
  await waitForIdle();
  const units = await database.units.toArray();
  await waitForIdle();
  const actions = await database.actions.toArray();
  await waitForIdle();
  const schemas = (await database.schemas?.toArray()) ?? [];
  await waitForIdle();
  const templates = (await database.templates?.toArray()) ?? [];
  await waitForIdle();
  const componentCategories = (await database.componentCategories?.toArray()) ?? [];
  await waitForIdle();
  const kv = (await database.kv?.toArray()) ?? [];
  await waitForIdle();
  const meta = (await database.meta?.toArray()) ?? [];
  await waitForIdle();
  const media = await serializeMedia(database);

  const tables: DataBundleTables = {
    reports,
    companies,
    sites,
    contacts,
    units,
    actions,
    schemas,
    templates,
    componentCategories,
    kv,
    meta,
    media,
  };

  const counts = buildCounts(tables);

  return {
    meta: {
      signature: DATA_BUNDLE_SIGNATURE,
      version: DATA_BUNDLE_VERSION,
      createdAt: new Date().toISOString(),
      schemaVersion,
      appVersion: APP_VERSION,
      featureFlags: { ...FEATURE_FLAGS },
      counts,
    },
    tables,
  } satisfies DataBundle;
}

export function serializeBundle(bundle: DataBundle): string {
  return JSON.stringify(bundle, null, 2);
}

export function buildExportFileName(now: Date = new Date()): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `industrial-audit-backup-${date}-${time}.json`;
}

export function parseBundleFromJson(raw: string): DataBundle {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new DataBundleError(
      "Il file selezionato non contiene JSON valido. Assicurati di caricare l'export generato dall'app."
    );
  }

  try {
    return bundleSchema.parse(parsed);
  } catch (error) {
    if (error instanceof ZodError) {
      const firstIssue = error.issues[0];
      const reason = firstIssue?.message ?? 'Struttura del file non valida';
      throw new DataBundleError(reason);
    }
    throw error;
  }
}

function validateCounts(bundle: DataBundle): void {
  const counts = buildCounts(bundle.tables);
  const mismatches = Object.entries(counts).filter(
    ([key, value]) => bundle.meta.counts[key as keyof DataBundleCounts] !== value
  );

  if (mismatches.length > 0) {
    const details = mismatches
      .map(
        ([key, value]) =>
          `${key}: atteso ${bundle.meta.counts[key as keyof DataBundleCounts]}, trovato ${value}`
      )
      .join(', ');
    throw new DataBundleError(
      `Conteggi delle tabelle non coerenti con la metadata del file (${details}). Il file potrebbe essere corrotto.`
    );
  }
}

type ForeignKeyError = {
  table: string;
  recordId: string;
  field: string;
  missingId: string;
  referencedTable: string;
};

function validateForeignKeys(bundle: DataBundle): void {
  const errors: ForeignKeyError[] = [];

  const companyIds = new Set(bundle.tables.companies.map((c) => c.id));
  const siteIds = new Set(bundle.tables.sites.map((s) => s.id));
  const reportIds = new Set(bundle.tables.reports.map((r) => r.id));

  // Validate sites reference valid companies
  for (const site of bundle.tables.sites) {
    if (!companyIds.has(site.companyId)) {
      errors.push({
        table: 'sites',
        recordId: site.id,
        field: 'companyId',
        missingId: site.companyId,
        referencedTable: 'companies',
      });
    }
  }

  // Validate reports reference valid companies and sites
  for (const report of bundle.tables.reports) {
    if (!companyIds.has(report.companyId)) {
      errors.push({
        table: 'reports',
        recordId: report.id,
        field: 'companyId',
        missingId: report.companyId,
        referencedTable: 'companies',
      });
    }
    if (!siteIds.has(report.siteId)) {
      errors.push({
        table: 'reports',
        recordId: report.id,
        field: 'siteId',
        missingId: report.siteId,
        referencedTable: 'sites',
      });
    }
  }

  // Validate units reference valid companies and sites
  for (const unit of bundle.tables.units) {
    if (!companyIds.has(unit.companyId)) {
      errors.push({
        table: 'units',
        recordId: unit.id,
        field: 'companyId',
        missingId: unit.companyId,
        referencedTable: 'companies',
      });
    }
    if (!siteIds.has(unit.siteId)) {
      errors.push({
        table: 'units',
        recordId: unit.id,
        field: 'siteId',
        missingId: unit.siteId,
        referencedTable: 'sites',
      });
    }
  }

  // Validate contacts reference valid companies (siteId is optional)
  for (const contact of bundle.tables.contacts) {
    if (!companyIds.has(contact.companyId)) {
      errors.push({
        table: 'contacts',
        recordId: contact.id,
        field: 'companyId',
        missingId: contact.companyId,
        referencedTable: 'companies',
      });
    }
    if (contact.siteId && !siteIds.has(contact.siteId)) {
      errors.push({
        table: 'contacts',
        recordId: contact.id,
        field: 'siteId',
        missingId: contact.siteId,
        referencedTable: 'sites',
      });
    }
  }

  // Validate actions reference valid companies (siteId and reportId are optional)
  for (const action of bundle.tables.actions) {
    if (!companyIds.has(action.companyId)) {
      errors.push({
        table: 'actions',
        recordId: action.id,
        field: 'companyId',
        missingId: action.companyId,
        referencedTable: 'companies',
      });
    }
    if (action.siteId && !siteIds.has(action.siteId)) {
      errors.push({
        table: 'actions',
        recordId: action.id,
        field: 'siteId',
        missingId: action.siteId,
        referencedTable: 'sites',
      });
    }
    if (action.reportId && !reportIds.has(action.reportId)) {
      errors.push({
        table: 'actions',
        recordId: action.id,
        field: 'reportId',
        missingId: action.reportId,
        referencedTable: 'reports',
      });
    }
  }

  // Validate media references valid reports
  for (const media of bundle.tables.media) {
    if (!reportIds.has(media.reportId)) {
      errors.push({
        table: 'media',
        recordId: media.id,
        field: 'reportId',
        missingId: media.reportId,
        referencedTable: 'reports',
      });
    }
  }

  if (errors.length > 0) {
    const summary = errors
      .slice(0, 3)
      .map((e) => `${e.table}.${e.field} (${e.recordId}) → ${e.referencedTable} (${e.missingId})`);
    const suffix = errors.length > 3 ? ` e altri ${errors.length - 3}` : '';
    throw new DataBundleError(
      `Riferimenti non validi nel file di import: ${summary.join(', ')}${suffix}. Il file potrebbe essere corrotto o incompleto.`
    );
  }
}

export function assertBundleCompatibility(bundle: DataBundle, database: AppDB = db): void {
  const schemaVersion = currentSchemaVersion(database);
  if (bundle.meta.schemaVersion !== schemaVersion) {
    throw new DataBundleError(
      `Versione schema non compatibile. File ${bundle.meta.schemaVersion}, applicazione ${schemaVersion}. Aggiorna l'app o il file di export prima di procedere.`
    );
  }

  validateCounts(bundle);
  validateForeignKeys(bundle);
}

function toReportRow(row: SerializedReportRow): ReportRow {
  return {
    id: row.id,
    version: row.version,
    companyId: row.companyId,
    siteId: row.siteId,
    payload: row.payload,
    lockedBy: row.lockedBy,
    lockedAt: row.lockedAt,
    lockedDevice: row.lockedDevice,
    syncUpdatedAt: row.syncUpdatedAt,
    syncDeletedAt: row.syncDeletedAt,
    syncDeviceId: row.syncDeviceId,
    syncVersion: row.syncVersion,
  } satisfies ReportRow;
}

function toMediaRow(row: SerializedMediaRow): MediaRow {
  return {
    id: row.id,
    reportId: row.reportId,
    storagePath: row.storagePath,
    blob: dataUrlToBlob(row.dataUrl),
    mimeType: row.mimeType,
    byteSize: row.byteSize,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  } satisfies MediaRow;
}

export async function replaceDatabaseFromBundle(
  bundle: DataBundle,
  database: AppDB = db
): Promise<void> {
  assertBundleCompatibility(bundle, database);

  const reports = bundle.tables.reports.map(toReportRow);
  const mediaRows = bundle.tables.media.map(toMediaRow);

  await database.transaction(
    'rw',
    [
      database.companies,
      database.sites,
      database.contacts,
      database.units,
      database.reports,
      database.actions,
      database.schemas,
      database.templates,
      database.componentCategories,
      database.kv,
      database.meta,
      database.media,
    ],
    async () => {
      await Promise.all([
        database.companies.clear(),
        database.sites.clear(),
        database.contacts.clear(),
        database.units.clear(),
        database.reports.clear(),
        database.actions.clear(),
        database.schemas?.clear?.(),
        database.templates?.clear?.(),
        database.componentCategories?.clear?.(),
        database.kv?.clear?.(),
        database.meta?.clear?.(),
        database.media?.clear?.(),
      ]);

      await database.companies.bulkAdd(bundle.tables.companies);
      await database.sites.bulkAdd(bundle.tables.sites);
      await database.contacts.bulkAdd(bundle.tables.contacts);
      await database.units.bulkAdd(bundle.tables.units);
      await database.actions.bulkAdd(bundle.tables.actions);
      if (database.schemas) {
        await database.schemas.bulkAdd(bundle.tables.schemas);
      }
      if (database.templates) {
        await database.templates.bulkAdd(bundle.tables.templates);
      }
      if (database.componentCategories) {
        await database.componentCategories.bulkAdd(bundle.tables.componentCategories);
      }
      if (database.kv) {
        await database.kv.bulkAdd(bundle.tables.kv);
      }
      if (database.meta) {
        await database.meta.bulkAdd(bundle.tables.meta);
      }
      if (database.media) {
        await database.media.bulkAdd(mediaRows);
      }
      await database.reports.bulkAdd(reports);
    }
  );
}

export function describeDataBundleError(error: unknown): string {
  if (error instanceof DataBundleError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Si è verificato un errore sconosciuto durante l'elaborazione del file.";
}

export const DATA_BUNDLE_CONSTANTS = {
  signature: DATA_BUNDLE_SIGNATURE,
  version: DATA_BUNDLE_VERSION,
} as const;
